import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const providerIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/)

export const worklioBranchMappingV1Schema = z.object({
  mappingId: entityIdSchema,
  branchId: branchIdSchema,
  providerLocationId: providerIdSchema,
  providerDivisionId: providerIdSchema,
}).strict()

export const worklioMappingConfigureV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().nonnegative(),
  mappingId: entityIdSchema,
  revisionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  providerCompanyId: providerIdSchema,
  erpnextSupplier: z.string().trim().min(1).max(140),
  branches: z.array(worklioBranchMappingV1Schema).min(1).max(100),
}).strict().superRefine((value, context) => {
  const unique = (items: string[]) => new Set(items).size === items.length
  if (
    !unique(value.branches.map((item) => item.mappingId)) ||
    !unique(value.branches.map((item) => item.branchId)) ||
    !unique(value.branches.map((item) => item.providerLocationId)) ||
    !unique(value.branches.map((item) => item.providerDivisionId))
  ) {
    context.addIssue({
      code: "custom",
      message: "Worklio Branch mappings must be unique.",
      path: ["branches"],
    })
  }
})

export const worklioMappingResultV1Schema = z.object({
  entityType: z.literal("worklio_company_mapping"),
  entityId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  state: z.enum(["pending_verification", "verified", "reconciliation_required"]),
  version: z.number().int().positive(),
  revisionId: entityIdSchema,
  providerCompanyRef: z.string().regex(/^[a-f0-9]{16}$/),
  erpnextSupplier: z.string().trim().min(1).max(140),
  branchCount: z.number().int().positive(),
  providerIdsScoped: z.literal(true),
}).strict()

export const worklioMappingReconcileV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().positive(),
  mappingId: entityIdSchema,
  revisionId: entityIdSchema,
  activationId: entityIdSchema,
  activationTransitionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
}).strict()

export const worklioMappingReconcileResultV1Schema = z.object({
  entityType: z.literal("worklio_company_mapping"),
  entityId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  state: z.enum(["pending_verification", "verified", "reconciliation_required"]),
  version: z.number().int().positive(),
  activationState: z.enum(["action_required", "active"]),
  activationVersion: z.number().int().positive(),
  taxSetupComplete: z.boolean(),
  bankSetupComplete: z.boolean(),
  missingLocationCount: z.number().int().nonnegative(),
  missingDivisionCount: z.number().int().nonnegative(),
  providerIdsScoped: z.literal(true),
}).strict()

export const worklioCompanyProvisioningResultV1Schema = z.object({
  providerCompanyId: providerIdSchema,
  providerCompanyRef: z.string().regex(/^[a-f0-9]{16}$/),
  companyReused: z.boolean(),
  taxSetupComplete: z.boolean(),
  bankSetupComplete: z.boolean(),
  readyForActivation: z.boolean(),
  branches: z.array(worklioBranchMappingV1Schema).min(1).max(100),
  sensitiveValuesReturned: z.literal(false),
}).strict().superRefine((value, context) => {
  if (
    value.readyForActivation
    !== (value.taxSetupComplete && value.bankSetupComplete)
  ) {
    context.addIssue({
      code: "custom",
      message: "Worklio Company readiness is inconsistent.",
      path: ["readyForActivation"],
    })
  }
})

export type WorklioBranchMappingV1 = z.infer<typeof worklioBranchMappingV1Schema>
export type WorklioCompanyProvisioningResultV1 = z.infer<
  typeof worklioCompanyProvisioningResultV1Schema
>
export type WorklioMappingConfigureV1 = z.infer<
  typeof worklioMappingConfigureV1Schema
>
export type WorklioMappingResultV1 = z.infer<typeof worklioMappingResultV1Schema>
export type WorklioMappingReconcileV1 = z.infer<
  typeof worklioMappingReconcileV1Schema
>
export type WorklioMappingReconcileResultV1 = z.infer<
  typeof worklioMappingReconcileResultV1Schema
>
